"use client";

import React from "react";
import { RefreshCw, ReceiptText } from "lucide-react";
import { useSelector } from "react-redux";
import type { ColumnDef } from "@tanstack/react-table";
import type { RootState } from "@/store/store";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { useDashboardPanel } from "@/hooks/useDashboardPanel";
import { Button } from "@/components/ui/button";

interface Transaction {
  id: string;
  customer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
  };
  order_number?: number;
  created_at?: string;
  updated_at?: string;
  current_order_stage?: string;
  current_payment_stage?: string;
  items?: Array<{ quantity?: number }> | number;
  calculation?: {
    grand_total?: number;
    grand_total_int?: number;
    items_sub_total?: number;
  };
  shipping_address?: {
    city?: string;
    state?: string;
  };
}

function formatMoney(amount: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function getOrderTotal(order: Transaction) {
  const calc = order.calculation;
  if (typeof calc?.grand_total === "number" && calc.grand_total > 0)
    return calc.grand_total;
  if (typeof calc?.grand_total_int === "number" && calc.grand_total_int > 0)
    return calc.grand_total_int / 100;
  if (typeof calc?.items_sub_total === "number" && calc.items_sub_total > 0)
    return calc.items_sub_total / 100;
  return 0;
}

function getCustomerName(customer?: Transaction["customer"]) {
  if (!customer) return "N/A";
  const name =
    `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  return name || customer.email || customer.phone_number || "N/A";
}

function getItemsCount(items?: Transaction["items"]) {
  if (typeof items === "number") return items;
  if (Array.isArray(items)) {
    return items.reduce((acc, item) => acc + (item?.quantity || 1), 0);
  }
  return 0;
}

export default function TransactionHistory() {
  const { refreshPanel, isRefreshing } = useDashboardPanel("transactions");
  const reduxTransactions = useSelector(
    (state: RootState) => state.dashboard?.transactions ?? [],
  );
  const items: Transaction[] =
    reduxTransactions && Array.isArray(reduxTransactions)
      ? (reduxTransactions as Transaction[])
      : [];

  const columns: ColumnDef<Transaction>[] = [
    {
      accessorKey: "id",
      header: "TRANSACTION",
      cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {`#${row.original.id.slice(-6)}`}
            </span>
            {row.original.order_number ? (
              <span className="text-xs text-muted-foreground">
                Order #{row.original.order_number}
              </span>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            {row.original.created_at
              ? new Date(row.original.created_at).toLocaleDateString()
              : "No timestamp"}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "customer",
      header: "CUSTOMER",
      cell: ({ row }) => {
        const customer: any = row.original.customer;
        return (
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              {getCustomerName(customer)}
            </p>
            <p className="text-xs text-muted-foreground">
              {customer?.phone_number || customer?.email || "No contact"}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "current_payment_stage",
      header: "PAYMENT",
      cell: ({ row }) => (
        <div className="space-y-1">
          <span className="inline-flex rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            {String(
              row.original.current_payment_stage || "created",
            ).toLowerCase()}
          </span>
          <p className="text-xs text-muted-foreground">
            {String(
              row.original.current_order_stage || "created",
            ).toLowerCase()}{" "}
            order stage
          </p>
        </div>
      ),
    },
    {
      accessorKey: "items",
      header: "ITEMS",
      cell: ({ row }) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <ReceiptText className="h-3.5 w-3.5 text-muted-foreground" />
            {getItemsCount(row.original.items)} item
            {getItemsCount(row.original.items) === 1 ? "" : "s"}
          </div>
          <p className="text-xs text-muted-foreground">
            {row.original.shipping_address?.city ||
              row.original.shipping_address?.state ||
              "No route"}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "amount",
      header: "AMOUNT",
      cell: ({ row }) => (
        <div className="space-y-1">
          <span className="font-semibold text-foreground">
            {formatMoney(getOrderTotal(row.original))}
          </span>
          <p className="text-xs text-muted-foreground">
            {row.original.updated_at
              ? `Updated ${new Date(row.original.updated_at).toLocaleDateString()}`
              : "Final amount pending"}
          </p>
        </div>
      ),
    },
  ];

  return (
    <TableContainer
      className="w-full"
      headerLeft={
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">
            Transactions
          </h2>
          <p className="text-xs text-muted-foreground">
            Total {items.length} transaction{items.length !== 1 ? "s" : ""} with
            payment stage, customer, items, and amount details
          </p>
        </div>
      }
      headerRight={
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshPanel}
          disabled={isRefreshing}
          className="h-8 w-8 p-0"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </Button>
      }
    >
      <DataView<Transaction, unknown>
        columns={columns}
        data={items}
        title=""
        pagination={{
          page: 0,
          total: items.length,
          pageSize: items.length,
          totalPages: 1,
        }}
        pageIndex={0}
        onPageChange={() => {}}
        loading={false}
        cardWrapper
        cardWrapperClassName="border-border/60 bg-card/85 backdrop-blur-xl shadow-theme-sm"
        emptyMessage="No transactions found."
      />
    </TableContainer>
  );
}
